import React, { useState, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import {
  Select,
  MenuItem,
  FormControl,
  Typography,
  TextField,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import { useField, ErrorMessage } from "formik";
import { styled } from "@mui/material/styles";
import FormikLabel from "./FormikLabel";
import { ErrorStyle } from "./ErrorStyle";
import KeyboardArrowDownSharpIcon from "@mui/icons-material/KeyboardArrowDownSharp";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const CustomSelect = styled(Select)(({ disabled, readOnly }) => ({
  cursor: disabled || readOnly ? "not-allowed" : "inherit",
  "& .MuiSelect-select": {
    padding: "8px",
    display: "flex",
    alignItems: "center",
    backgroundColor: disabled || readOnly ? "#f5f5f5" : "inherit",
    cursor: disabled || readOnly ? "not-allowed" : "inherit",
  },
  "&.Mui-disabled .MuiSelect-select": {
    cursor: "not-allowed",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: "8px",
  },
}));

const FormikSelectInput = ({
  name = "",
  label = "",
  options = [],
  width = "100%",
  required = false,
  placeholder = "Select...",
  onChange = () => {},
  sx = {},
  disabled = false,
  readOnly = false,
  search = false,
  clear = false,
  menuWidth = "100%",
  truncateLength = 100,
  icon = null,
  onLabelClick = null,
  ...props
}) => {
  const [field, , helpers] = useField(name);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [menuPlacement, setMenuPlacement] = useState("bottom");
  const [shouldClearOnOpen, setShouldClearOnOpen] = useState(false);

  const selectRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    return options.filter((option) => {
      const label =
        typeof option === "string"
          ? option
          : option.label
          ? option.label
          : option.name;

      return (
        typeof label === "string" &&
        label.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [search, options, searchTerm]);

  const handleOpen = () => {
    setOpen(true);

    const rect = selectRef.current?.getBoundingClientRect?.();
    if (!rect) return;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    setMenuPlacement(
      spaceBelow < 200 && spaceAbove > spaceBelow ? "top" : "bottom"
    );

    if (shouldClearOnOpen) {
      helpers.setValue("");
      onChange({ target: { value: "" } });
      setShouldClearOnOpen(false);
    }

    if (search) {
      setSearchTerm("");
    }
  };

  return (
    <FormControl fullWidth sx={{ width, ...sx, position: "relative" }} ref={selectRef}>
      <FormikLabel
        text={label}
        required={required}
        icon={icon}
        onClick={onLabelClick}
      />
      <CustomSelect
        {...field}
        {...props}
        value={field.value || ""}
        onChange={(e) => {
          if (!readOnly) {
            onChange(e);
            helpers?.setValue(e.target.value);
          }
        }}
        open={open}
        inputRef={selectRef}
        onOpen={handleOpen}
        onClose={() => {
          setOpen(false);
          setShouldClearOnOpen(true);
        }}
        disabled={disabled}
        displayEmpty
        IconComponent={
          clear && !readOnly && field.value
            ? () => null
            : KeyboardArrowDownSharpIcon
        }
        renderValue={(selected) => {
          if (!selected) {
            return (
              <Typography sx={{ color: "rgba(0, 0, 0, 0.54)" }}>
                {placeholder}
              </Typography>
            );
          }

          const selectedOption = options.find((opt) =>
            typeof opt === "string" ? opt === selected : opt.value === selected
          );

          const label =
            typeof selectedOption === "string"
              ? selectedOption
              : selectedOption?.label || selectedOption?.name || selected;

          return label?.length > truncateLength
            ? `${label.slice(0, truncateLength)}...`
            : label;
        }}
        input={
          <OutlinedInput
            onClick={(e) => {
              e.stopPropagation(); // optional: prevent MUI from toggling too early
              setSearchTerm(""); // call your custom click handler
            }}
            endAdornment={
              clear && !readOnly && field.value ? (
                <InputAdornment
                  position="end"
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 1,
                  }}
                >
                  <ClearIcon
                    fontSize="small"
                    sx={{ cursor: "pointer", color: "#999" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      helpers.setValue("");
                      onChange({ target: { value: "" } });
                    }}
                  />
                </InputAdornment>
              ) : null
            }
          />
        }
        sx={{
          // height: "40px",
          ".MuiOutlinedInput-input": {
            padding: "8px !important",
          },
          borderRadius: "8px",
          width: "100%",
          ...sx,
        }}
        readOnly={readOnly}
        MenuProps={{
          autoFocus: false,
          disableAutoFocus: true,
          disableEnforceFocus: true,
          disableAutoFocusItem: true,
          disablePortal: true,
          TransitionProps: {
            onEntered: () => {
              if (search && searchInputRef.current) {
                searchInputRef.current.focus();
              }
            },
          },
          PaperProps: {
            onMouseDown: (e) => {
              // Prevent MUI from stealing focus away from search input
              const isSearchArea = e.target.closest('[data-search-container]');
              if (isSearchArea) e.preventDefault();
            },
            style: {
              zIndex: 1300,
              margin: "0px",
            },
          },
          anchorOrigin: {
            vertical: menuPlacement,
            horizontal: "left",
          },
          transformOrigin: {
            vertical: menuPlacement === "bottom" ? "top" : "bottom",
            horizontal: "left",
          },
          getContentAnchorEl: null,
        }}
      >
        <>
          {search && (
            <div
              data-search-container
              style={{
                padding: "8px 16px",
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 2,
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                searchInputRef.current?.focus();
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <TextField
                fullWidth
                placeholder="Search..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                inputRef={searchInputRef}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    return;
                  }
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    const firstItem = listRef.current?.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
                    if (firstItem) firstItem.focus();
                    return;
                  }
                  e.stopPropagation();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}

          <div
            ref={listRef}
            style={{
              maxHeight: 160,
              overflowY: "auto",
            }}
          >
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>

            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const optionValue =
                  typeof option === "string"
                    ? option
                    : option.value ?? option.name;

                const optionLabel =
                  typeof option === "string"
                    ? option
                    : option.label ?? option.name;

                return (
                  <MenuItem
                    key={optionValue}
                    value={optionValue}
                    disabled={readOnly}
                    onClick={(e) => {
                      if (!readOnly) {
                        const fakeEvent = { target: { value: optionValue } };
                        onChange(fakeEvent); // <-- trigger external onChange
                        helpers?.setValue(optionValue);
                        setOpen(false);
                      }
                    }}
                    sx={{
                      maxWidth: "100%",
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: menuWidth,
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {optionLabel}
                    </div>
                  </MenuItem>
                );
              })
            ) : (
              <MenuItem disabled>No results found</MenuItem>
            )}
          </div>
        </>
      </CustomSelect>

      <div style={ErrorStyle}>
        <ErrorMessage name={name} />
      </div>
    </FormControl>
  );
};

FormikSelectInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        label: PropTypes.string.isRequired,
      }),
    ])
  ).isRequired,
  width: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  sx: PropTypes.object,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  search: PropTypes.bool,
  clear: PropTypes.bool,
};

export default FormikSelectInput;
